import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Send as SendIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import type { ColumnDef } from '../common/tableFeatures';
import { clipped, dateSortValue, dateTime, formatBytes, noActions } from '../common/tableCells';
import type { DotNetAppUpdate } from '@store/api/dotnetAppUpdatesApi';

interface DotNetUpdateColumnHandlers {
  onDeploy: (version: string) => void;
  onDownload: (version: string) => void;
  onDelete: (version: string) => void;
}

/** TanStack column definitions for the .NET app-update table. */
export const createDotNetUpdateColumns = ({
  onDeploy,
  onDownload,
  onDelete,
}: DotNetUpdateColumnHandlers): ColumnDef<DotNetAppUpdate>[] => [
  {
    id: 'version',
    accessorKey: 'version',
    header: 'Version',
    cell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.original.version}
        </Typography>
        {row.original.is_deleted && (
          <Chip label="Deleted" size="small" variant="outlined" />
        )}
      </Box>
    ),
    size: 160,
  },
  {
    id: 'filename',
    accessorKey: 'filename',
    header: 'Filename',
    cell: ({ row }) => clipped(row.original.filename, 260, { mono: true }),
    size: 280,
  },
  {
    id: 'size',
    accessorKey: 'size',
    header: 'Size',
    cell: ({ row }) => (row.original.is_deleted ? 'N/A' : formatBytes(row.original.size)),
    size: 100,
  },
  {
    id: 'has_signature',
    accessorFn: (row) => (row.has_signature ? 1 : 0),
    header: 'Signature',
    cell: ({ row }) => {
      const update = row.original;
      if (update.is_deleted) return <Chip label="Deleted" variant="outlined" size="small" />;
      return update.has_signature ? (
        <Chip icon={<SuccessIcon />} label="Signed" color="success" size="small" />
      ) : (
        <Chip label="No Signature" color="error" size="small" />
      );
    },
    size: 140,
  },
  {
    id: 'modified_time',
    accessorFn: (row) => dateSortValue(row.modified_time),
    header: 'Upload Date',
    cell: ({ row }) => dateTime(row.original.modified_time),
    size: 170,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const update = row.original;
      if (update.is_deleted) {
        return noActions();
      }
      return (
        <>
          <Tooltip title="Deploy to devices">
            <IconButton
              size="small"
              onClick={() => onDeploy(update.version)}
              disabled={!update.has_signature}
              aria-label={`Deploy version ${update.version} to devices`}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => onDownload(update.version)}
              aria-label={`Download version ${update.version}`}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(update.version)}
              aria-label={`Delete version ${update.version}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      );
    },
    enableSorting: false,
    size: 150,
    meta: { sticky: 'right' },
  },
];
